/**
 * Chat screen view component
 * @remarks
 * Pure UI component for rendering chat interface with mobile/desktop layouts.
 * Receives all data and callbacks as props from ChatContainer.
 * Uses memo() for performance optimization.
 * @param props - All chat view props
 * @param props.isDark - Whether dark theme is enabled
 * @param props.messages - Array of chat messages
 * @param props.isLoading - Whether query is in progress
 * @param props.error - Error message if query failed
 * @param props.connectionStatus - SSE connection status
 * @param props.reconnectAttempts - Number of reconnection attempts
 * @param props.showStatusSettings - Whether status settings modal is visible
 * @param props.isMobile - Narrow layout (the container's `useMobileScreen()`)
 * @param props.headerHeight - Header height for keyboard offset
 * @param props.bottomInset - Bottom safe area inset
 * @param props.androidKeyboardHeight - Android keyboard height (0 when closed)
 * @param props.tabBarHeight - Bottom tab bar height below this screen
 * @param props.keyboardHeight - Animated keyboard height value
 * @param props.inputAnimatedStyle - Animated style for input positioning
 * @param props.colors - Theme colors from useChatTheme
 * @param props.onSend - Callback when user sends a message (for GiftedChat)
 * @param props.onToggleStatusSettings - Callback to toggle status settings modal
 * @param props.onSuggestedQuestionPress - Callback when suggested question is pressed
 * @param props.renderBubble - Render function for message bubbles
 * @param props.renderSend - Render function for send button
 * @param props.renderInputToolbar - Render function for input toolbar
 * @param props.renderFooter - Render function for chat footer
 * @param props.renderChatEmpty - Render function for empty chat state
 * @param props.renderSuggestedQuestions - Render function for suggested questions
 * @param props.mobileErrorDisplay - Pre-rendered mobile error display element
 * @param props.mobileFlatList - Pre-rendered mobile FlatList element
 * @param props.mobileChatInput - Pre-rendered mobile ChatInput element
 */

import React, { memo } from "react";
// design-system: rn-primitive waiver — dynamic style / behavior-coupled
import {
  Platform,
  // eslint-disable-next-line no-restricted-imports -- Platform.OS==='web'?Animated.View:View capability fork; native branch needs raw RN View to host a reanimated animated-style array no atom can express
  View,
  type ViewStyle,
} from "react-native";
import {
  BubbleProps,
  GiftedChat,
  IMessage,
  InputToolbarProps,
  SendProps,
  User,
} from "react-native-gifted-chat";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
import Animated, {
  SharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";

import { Box, Text } from "@/components/atoms";
import ConnectionStatus from "@/features/knowledge-graph/components/molecules/ConnectionStatus";
import { StatusSettings } from "@/features/knowledge-graph/components/molecules/StatusSettings";

import type { ConnectionStatus as ConnectionStatusType } from "@/stores/knowledgeGraph";
import { TEST_ID_GEMINI_CHAT } from "e2e/testId";

/**
 * Input container component - uses Animated.View on web, regular View on native
 * to avoid Reanimated 4.x animated style issues
 */
const InputContainer = Platform.OS === "web" ? Animated.View : View;

/**
 * Theme colors from useChatTheme hook
 */
interface ChatThemeColors {
  messageBubble: {
    background: string;
    userBackground: string;
    userText: string;
  };
}

/**
 * Props interface for ChatView component
 */
interface ChatViewProps {
  readonly isDark: boolean;
  readonly messages: readonly IMessage[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly connectionStatus: ConnectionStatusType;
  readonly reconnectAttempts: number;
  readonly showStatusSettings: boolean;
  /** Narrow layout — the container's `useMobileScreen()` (SE-8674). */
  readonly isMobile: boolean;
  readonly headerHeight: number;
  readonly bottomInset: number;
  /** Android keyboard height (0 when closed) — pads the in-flow input above the overlaying keyboard */
  readonly androidKeyboardHeight: number;
  /** Bottom tab bar height below this screen (native keyboard clearance) */
  readonly tabBarHeight: number;
  readonly keyboardHeight: SharedValue<number>;
  readonly inputAnimatedStyle: ReturnType<typeof useAnimatedStyle>;
  /** Static keyboard height for native (avoids Reanimated 4.x animated style issues) */
  readonly staticKeyboardHeight: number;
  readonly colors: ChatThemeColors;
  readonly onSend: (newMessages: readonly IMessage[]) => void;
  readonly onToggleStatusSettings: () => void;
  readonly onSuggestedQuestionPress: (query: string) => void;
  readonly renderBubble: (
    props: BubbleProps<IMessage>
  ) => React.JSX.Element | null;
  readonly renderSend: (props: SendProps<IMessage>) => React.JSX.Element;
  readonly renderInputToolbar: (
    props: InputToolbarProps<IMessage>
  ) => React.JSX.Element;
  readonly renderFooter: () => React.JSX.Element | null;
  readonly renderChatEmpty: () => React.JSX.Element | null;
  readonly renderSuggestedQuestions: () => React.JSX.Element;
  /** Pre-reversed messages for GiftedChat (desktop layout) */
  readonly reversedMessages: IMessage[];
  readonly mobileErrorDisplay: React.JSX.Element | null;
  readonly mobileFlatList: React.JSX.Element;
  readonly mobileChatInput: React.JSX.Element;
}

/**
 * Current user for message sending
 */
const currentUser: User = {
  _id: "user",
  name: "You",
};

/**
 * Utility render function that returns null for GiftedChat props
 */
const renderNull = () => null;

/*
 * design-system: dynamic-style waiver — the KeyboardAvoidingView fill and the
 * measured Android keyboard-clearance padding are the only runtime style values
 * left; theme colours and error styling now resolve through semantic tokens.
 */
const FLEX_FILL_STYLE: ViewStyle = { flex: 1 };
/**
 * SE-5440: Android edge-to-edge (SDK 56 / Android 15) ignores
 * `softwareKeyboardLayoutMode: "resize"`, so the open keyboard overlays the
 * window and the input must be padded above it manually. The screen bottom
 * already sits `tabBarHeight` above the window bottom (the tab bar also
 * consumes the bottom safe-area inset), so the required clearance is
 * `keyboardHeight - tabBarHeight`, not `keyboardHeight + 60` /
 * `bottomInset + 8` — the old constants double-counted the tab bar and
 * inset, painting a large dead band between the input and the nav bar
 * and lifting the input ~150dp above the open keyboard.
 * @param androidKeyboardHeight - Current keyboard height (0 when closed)
 * @param tabBarHeight - Bottom tab bar height below this screen
 */
const inputWrapPaddingStyle = (
  androidKeyboardHeight: number,
  tabBarHeight: number
): ViewStyle => ({
  paddingBottom:
    androidKeyboardHeight > 0
      ? Math.max(8, androidKeyboardHeight - tabBarHeight + 8)
      : 8,
});

/**
 * Renders Android mobile layout — input in normal flow below the list,
 * manually padded above the overlaying keyboard (SE-5440).
 * @param props - Component props
 * @param props.connectionStatus - SSE connection status
 * @param props.reconnectAttempts - Number of reconnection attempts
 * @param props.mobileErrorDisplay - Pre-rendered error display
 * @param props.mobileFlatList - Pre-rendered message list
 * @param props.mobileChatInput - Pre-rendered chat input
 * @param props.androidKeyboardHeight - Current keyboard height
 * @param props.tabBarHeight - Bottom tab bar height below this screen
 */
function renderAndroidLayout({
  connectionStatus,
  reconnectAttempts,
  mobileErrorDisplay,
  mobileFlatList,
  mobileChatInput,
  androidKeyboardHeight,
  tabBarHeight,
}: Readonly<{
  connectionStatus: ConnectionStatusType;
  reconnectAttempts: number;
  mobileErrorDisplay: React.JSX.Element | null;
  mobileFlatList: React.JSX.Element;
  mobileChatInput: React.JSX.Element;
  androidKeyboardHeight: number;
  tabBarHeight: number;
}>) {
  return (
    <Box className="flex-1 bg-surface-base">
      <ConnectionStatus
        connectionState={connectionStatus}
        reconnectAttempts={reconnectAttempts}
      />
      {mobileErrorDisplay}
      {mobileFlatList}
      <Box
        className="bg-surface-base"
        // eslint-disable-next-line no-restricted-syntax -- measured Android keyboard-clearance paddingBottom (Math.max(8, kbHeight - tabBar + 8)); continuous runtime value, no closed spacing token
        UNSAFE_style={
          inputWrapPaddingStyle(androidKeyboardHeight, tabBarHeight) as Record<
            string,
            string | number
          >
        }
      >
        {mobileChatInput}
      </Box>
    </Box>
  );
}

/**
 * SE-8807: since SE-3726 the chat screen sits inside the tab navigator, so its
 * bottom edge is already `tabBarHeight` above the window bottom and the tab bar
 * covers the home-indicator inset. iOS reports the keyboard height from the
 * WINDOW bottom, so the native composer rises by `keyboardHeight -
 * tabBarHeight` and pads by 8 only — the raw height plus `bottomInset + 8`
 * floated it a tab bar above the keyboard over a dead inset band (the iOS twin
 * of SE-5440). Web keeps its animated bottom and inset padding.
 * @param staticKeyboardHeight - Native keyboard height (0 when closed)
 * @param tabBarHeight - Bottom tab bar height below this screen
 * @param bottomInset - Bottom safe area inset (web only)
 */
const iosComposerOffsetStyle = (
  staticKeyboardHeight: number,
  tabBarHeight: number,
  bottomInset: number
): ViewStyle =>
  Platform.OS === "web"
    ? { bottom: 0, paddingBottom: bottomInset + 8 }
    : {
        bottom: Math.max(0, staticKeyboardHeight - tabBarHeight),
        paddingBottom: 8,
      };

/**
 * Renders iOS mobile layout with animated positioning
 * @param props - Component props
 * @param props.isDark - Whether dark theme is enabled
 * @param props.connectionStatus - SSE connection status
 * @param props.reconnectAttempts - Number of reconnection attempts
 * @param props.mobileErrorDisplay - Pre-rendered error display
 * @param props.mobileFlatList - Pre-rendered message list
 * @param props.mobileChatInput - Pre-rendered chat input
 * @param props.bottomInset - Bottom safe area inset
 * @param props.inputAnimatedStyle - Animated style for input positioning
 * @param props.staticKeyboardHeight - Static keyboard height for native
 * @param props.tabBarHeight - Bottom tab bar height below this screen
 */
function renderIOSLayout({
  isDark,
  connectionStatus,
  reconnectAttempts,
  mobileErrorDisplay,
  mobileFlatList,
  mobileChatInput,
  bottomInset,
  inputAnimatedStyle,
  staticKeyboardHeight,
  tabBarHeight,
}: Readonly<{
  isDark: boolean;
  connectionStatus: ConnectionStatusType;
  reconnectAttempts: number;
  mobileErrorDisplay: React.JSX.Element | null;
  mobileFlatList: React.JSX.Element;
  mobileChatInput: React.JSX.Element;
  bottomInset: number;
  inputAnimatedStyle: ReturnType<typeof useAnimatedStyle>;
  staticKeyboardHeight: number;
  tabBarHeight: number;
}>) {
  return (
    <Box className="relative flex-1 bg-surface-base">
      <ConnectionStatus
        connectionState={connectionStatus}
        reconnectAttempts={reconnectAttempts}
      />
      {mobileErrorDisplay}
      {mobileFlatList}
      <InputContainer
        testID={TEST_ID_GEMINI_CHAT.MOBILE_COMPOSER}
        // eslint-disable-next-line no-restricted-syntax -- InputContainer is Platform.OS==='web'?Animated.View:View; hosts a reanimated animated-style array + measured absolute/safe-area positioning no atom can express
        style={[
          {
            position: "absolute",
            left: 0,
            right: 0,
            ...iosComposerOffsetStyle(
              staticKeyboardHeight,
              tabBarHeight,
              bottomInset
            ),
            backgroundColor: isDark ? "#000" : "#fff",
          },
          // Use animated style on web, static style on native
          Platform.OS === "web" ? (inputAnimatedStyle as object) : undefined,
        ]}
      >
        {mobileChatInput}
      </InputContainer>
    </Box>
  );
}

/**
 * Renders desktop layout with GiftedChat
 * @param props - Component props
 * @param props.isDark - Whether dark theme is enabled
 * @param props.connectionStatus - SSE connection status
 * @param props.reconnectAttempts - Number of reconnection attempts
 * @param props.error - Error message if any
 * @param props.messages - Chat messages (original order)
 * @param props.reversedMessages - Pre-reversed messages for GiftedChat
 * @param props.isLoading - Whether query is in progress
 * @param props.headerHeight - Header height for keyboard offset
 * @param props.onSend - Message send callback
 * @param props.renderBubble - Message bubble render function
 * @param props.renderSend - Send button render function
 * @param props.renderInputToolbar - Input toolbar render function
 * @param props.renderFooter - Chat footer render function
 * @param props.renderChatEmpty - Empty chat state render function
 * @param props.renderSuggestedQuestions - Suggested questions render function
 * @param props.showStatusSettings - Whether status settings modal is visible
 * @param props.onToggleStatusSettings - Status settings toggle callback
 */
function renderDesktopLayout({
  isDark,
  connectionStatus,
  reconnectAttempts,
  error,
  messages,
  reversedMessages,
  isLoading,
  headerHeight,
  onSend,
  renderBubble,
  renderSend,
  renderInputToolbar,
  renderFooter,
  renderChatEmpty,
  renderSuggestedQuestions,
  showStatusSettings,
  onToggleStatusSettings,
}: Readonly<{
  isDark: boolean;
  connectionStatus: ConnectionStatusType;
  reconnectAttempts: number;
  error: string | null;
  messages: readonly IMessage[];
  reversedMessages: IMessage[];
  isLoading: boolean;
  headerHeight: number;
  onSend: (newMessages: readonly IMessage[]) => void;
  renderBubble: (props: BubbleProps<IMessage>) => React.JSX.Element | null;
  renderSend: (props: SendProps<IMessage>) => React.JSX.Element;
  renderInputToolbar: (props: InputToolbarProps<IMessage>) => React.JSX.Element;
  renderFooter: () => React.JSX.Element | null;
  renderChatEmpty: () => React.JSX.Element | null;
  renderSuggestedQuestions: () => React.JSX.Element;
  showStatusSettings: boolean;
  onToggleStatusSettings: () => void;
}>) {
  return (
    <Box className="flex-1 bg-surface-base">
      <Box className="relative flex-1">
        <KeyboardAvoidingView
          // eslint-disable-next-line no-restricted-syntax -- KeyboardAvoidingView from react-native-keyboard-controller (behavior-coupled 3rd-party, no className/UNSAFE_style); flex:1 fill must ride a style ref
          style={FLEX_FILL_STYLE}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          keyboardVerticalOffset={Platform.OS === "ios" ? headerHeight : 0}
        >
          <ConnectionStatus
            connectionState={connectionStatus}
            reconnectAttempts={reconnectAttempts}
          />

          {Boolean(error) && (
            <Box className="px-2 pt-2">
              <Box className="rounded-sm border-l-4 border-status-error bg-status-error-surface p-2">
                <Text variant="body" className="text-status-error">
                  {error || "An error occurred"}
                </Text>
              </Box>
            </Box>
          )}

          <GiftedChat
            messages={reversedMessages}
            onSend={onSend}
            inverted
            user={currentUser}
            isTyping={isLoading}
            renderBubble={renderBubble}
            renderSend={renderSend}
            renderInputToolbar={renderInputToolbar}
            renderFooter={renderFooter}
            renderChatFooter={
              messages.length === 0 ? renderSuggestedQuestions : undefined
            }
            renderChatEmpty={renderChatEmpty}
            renderAvatar={renderNull}
            renderTime={renderNull}
            placeholder="Ask about player performance, scout reports..."
            textInputProps={{
              style: {
                color: isDark ? "#fff" : "#000",
                minHeight: 80,
                paddingTop: 2,
                paddingBottom: 16,
                fontSize: 16,
                lineHeight: 20,
                width: "100%",
                flex: 1,
                outlineStyle: "none",
                borderWidth: 0,
              },
              placeholderTextColor: isDark ? "#999" : "#666",
              multiline: true,
              accessibilityLabel: "Message input",
            }}
            messagesContainerStyle={{
              backgroundColor: isDark ? "#000" : "#fff",
              paddingHorizontal: 4,
            }}
            maxComposerHeight={150}
            minComposerHeight={80}
            alwaysShowSend={false}
          />
        </KeyboardAvoidingView>

        <StatusSettings
          visible={showStatusSettings}
          onClose={onToggleStatusSettings}
        />
      </Box>
    </Box>
  );
}

/**
 * Chat view component implementation
 * @param root0 - Component props
 * @param root0.isDark - Dark theme enabled
 * @param root0.messages - Chat messages
 * @param root0.isLoading - Query in progress
 * @param root0.error - Error message
 * @param root0.connectionStatus - SSE connection status
 * @param root0.reconnectAttempts - Reconnection attempts
 * @param root0.showStatusSettings - Status settings modal visible
 * @param root0.isMobile - Narrow layout (the container's `useMobileScreen()`)
 * @param root0.headerHeight - Header height for keyboard offset
 * @param root0.bottomInset - Bottom safe area inset
 * @param root0.androidKeyboardHeight - Android keyboard height (0 when closed)
 * @param root0.tabBarHeight - Bottom tab bar height below this screen
 * @param root0.inputAnimatedStyle - Animated style for input positioning
 * @param root0.staticKeyboardHeight - Static keyboard height for native
 * @param root0.onSend - Message send callback (for GiftedChat)
 * @param root0.onToggleStatusSettings - Status settings toggle callback
 * @param root0.onSuggestedQuestionPress - Suggested question press callback
 * @param root0.renderBubble - Message bubble render function
 * @param root0.renderSend - Send button render function
 * @param root0.renderInputToolbar - Input toolbar render function
 * @param root0.renderFooter - Chat footer render function
 * @param root0.renderChatEmpty - Empty chat state render function
 * @param root0.renderSuggestedQuestions - Suggested questions render function
 * @param root0.reversedMessages - Pre-reversed messages for GiftedChat desktop layout
 * @param root0.mobileErrorDisplay - Pre-rendered mobile error display element
 * @param root0.mobileFlatList - Pre-rendered mobile FlatList element
 * @param root0.mobileChatInput - Pre-rendered mobile ChatInput element
 */
const ChatView = ({
  isDark,
  messages,
  isLoading,
  error,
  connectionStatus,
  reconnectAttempts,
  showStatusSettings,
  isMobile,
  headerHeight,
  bottomInset,
  androidKeyboardHeight,
  tabBarHeight,
  inputAnimatedStyle,
  staticKeyboardHeight,
  onSend,
  onToggleStatusSettings,
  onSuggestedQuestionPress: _onSuggestedQuestionPress,
  renderBubble,
  renderSend,
  renderInputToolbar,
  renderFooter,
  renderChatEmpty,
  renderSuggestedQuestions,
  reversedMessages,
  mobileErrorDisplay,
  mobileFlatList,
  mobileChatInput,
}: ChatViewProps) =>
  (() => {
    const isAndroid = Platform.OS === "android";

    if (isMobile && isAndroid) {
      return renderAndroidLayout({
        connectionStatus,
        reconnectAttempts,
        mobileErrorDisplay,
        mobileFlatList,
        mobileChatInput,
        androidKeyboardHeight,
        tabBarHeight,
      });
    }

    if (isMobile) {
      return renderIOSLayout({
        isDark,
        connectionStatus,
        reconnectAttempts,
        mobileErrorDisplay,
        mobileFlatList,
        mobileChatInput,
        bottomInset,
        inputAnimatedStyle,
        staticKeyboardHeight,
        tabBarHeight,
      });
    }

    return renderDesktopLayout({
      isDark,
      connectionStatus,
      reconnectAttempts,
      error,
      messages,
      reversedMessages,
      isLoading,
      headerHeight,
      onSend,
      renderBubble,
      renderSend,
      renderInputToolbar,
      renderFooter,
      renderChatEmpty,
      renderSuggestedQuestions,
      showStatusSettings,
      onToggleStatusSettings,
    });
  })();

ChatView.displayName = "ChatView";

export default memo(ChatView);
